import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShoppingCart, Plus, Clock, CheckCircle, XCircle, Package, AlertCircle, Loader, Calendar, Printer, X, ZoomIn, ZoomOut, Edit, Eye } from 'lucide-react';
import CreateOrderForm from './CreateOrderForm';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import ColorDot from '../../components/common/ColorDot';
import CustomerOrderHistoryModal from '../../components/customer-management/CustomerOrderHistoryModal';

const CustomerOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  // URL'den tab parametresini oku ve aktif sekme olarak ayarla
  const getInitialSection = () => {
    if (tabParam) {
      const tabMap = {
        'pending': 'pending',
        'terminVerildi': 'terminVerildi',
        'dijitalBaski': 'dijitalBaski',
        'ready': 'ready',
        'completed': 'completed',
        'unavailable': 'unavailable',
        'cancelled': 'cancelled'
      };
      return tabMap[tabParam] || 'pending';
    }
    return 'pending';
  };
  
  const [activeSection, setActiveSection] = useState(getInitialSection()); // create, pending, terminVerildi, dijitalBaski, ready, completed, unavailable, cancelled
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyLastWeek, setOnlyLastWeek] = useState(true); // Sadece son 7 gün filtresi (tamamlanan, temin_edilemeyen, iptal_edilen)
  const [orderCounts, setOrderCounts] = useState({
    pending: 0,
    terminVerildi: 0,
    dijitalBaski: 0,
    ready: 0,
    completed: 0,
    unavailable: 0,
    cancelled: 0
  });
  /** Sekmede görünen siparişler arasında iptal talebi onayı bekleyen sayısı */
  const [cancelRequestBadges, setCancelRequestBadges] = useState({
    pending: 0,
    terminVerildi: 0,
    dijitalBaski: 0,
    ready: 0,
    completed: 0,
    unavailable: 0,
    cancelled: 0
  });
  const loadingRef = useRef(false); // Kısır döngüyü önlemek için

  const loadOrders = useCallback(async () => {
    if (activeSection === 'create') return; // Create section'da sipariş yükleme
    
    // Eğer zaten yükleniyorsa, önceki request'i iptal et ve yeni request başlat
    if (loadingRef.current) {
      // Önceki request devam ediyor, yeni request başlatmak için flag'i sıfırla
      loadingRef.current = false;
    }
    
    loadingRef.current = true;
    setLoading(true);
    
    // activeSection'ı local değişkene al (closure sorununu önlemek için)
    const currentSection = activeSection;
    
    try {
      const statusMap = {
        'pending': 'bekleyen',
        'terminVerildi': 'termin_verildi',
        'dijitalBaski': 'dijital_baski',
        'ready': 'teslimata_hazir',
        'completed': 'tamamlanan',
        'unavailable': 'temin_edilemeyen',
        'cancelled': 'iptal_edilen'
      };
      
      const status = statusMap[currentSection];
      if (!status) {
        console.warn('Bilinmeyen aktif sekme:', currentSection);
        setOrders([]);
        loadingRef.current = false;
        setLoading(false);
        return;
      }
      
      const response = await customerPortalService.getOrders(status);
      
      // Eğer activeSection değiştiyse, bu response'u kullanma
      if (currentSection !== activeSection) {
        console.log('ActiveSection değişti, response atlanıyor:', currentSection, '->', activeSection);
        return;
      }
      
      if (response.success) {
        let data = response.data || [];

        // Sadece belirli sekmelerde (tamamlanan, temin edilemeyen, iptal edilen) ve onlyLastWeek açıkken son 7 güne filtrele
        const shouldFilterLastWeek = onlyLastWeek && ['completed', 'unavailable', 'cancelled'].includes(currentSection);
        if (shouldFilterLastWeek) {
          const now = new Date();
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

          data = data.filter((order) => {
            let dateStr = null;
            if (status === 'tamamlanan') {
              dateStr = order.tamamlananDate || order.updatedAt || order.createdAt;
            } else if (status === 'temin_edilemeyen') {
              dateStr = order.teminEdilemeyenDate || order.updatedAt || order.createdAt;
            } else if (status === 'iptal_edilen') {
              dateStr = order.iptalEdilenDate || order.updatedAt || order.createdAt;
            }
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return false;
            return d >= sevenDaysAgo;
          });
        }

        setOrders(data);
      } else {
        console.error('Siparişler yüklenemedi - response:', response);
        toast.error(response.message || 'Siparişler yüklenemedi', { duration: 3000 });
        setOrders([]);
      }
    } catch (error) {
      // Eğer activeSection değiştiyse, hatayı görmezden gel
      if (currentSection !== activeSection) {
        console.log('ActiveSection değişti, hata atlanıyor:', currentSection, '->', activeSection);
        return;
      }
      
      console.error('Siparişler yüklenemedi:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Siparişler yüklenemedi';
      toast.error(errorMessage, { duration: 3000 });
      setOrders([]);
    } finally {
      // Sadece hala aynı section'daysa loading'i kapat
      if (currentSection === activeSection) {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [activeSection, onlyLastWeek]);

  // Siparişleri yükle
  useEffect(() => {
    // activeSection değiştiğinde siparişleri yükle
    if (activeSection !== 'create') {
      loadOrders();
    }
  }, [activeSection, loadOrders]);

  // Sipariş sayılarını yükle
  const loadOrderCounts = React.useCallback(async () => {
    try {
      const response = await customerPortalService.getOrders();
      if (response.success && response.data) {
        const allOrders = response.data;

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const filterLastWeekIfNeeded = (orders, status) => {
          if (!onlyLastWeek) return orders;
          if (!['tamamlanan', 'temin_edilemeyen', 'iptal_edilen'].includes(status)) return orders;

          return orders.filter((order) => {
            let dateStr = null;
            if (status === 'tamamlanan') {
              dateStr = order.tamamlananDate || order.updatedAt || order.createdAt;
            } else if (status === 'temin_edilemeyen') {
              dateStr = order.teminEdilemeyenDate || order.updatedAt || order.createdAt;
            } else if (status === 'iptal_edilen') {
              dateStr = order.iptalEdilenDate || order.updatedAt || order.createdAt;
            }
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return false;
            return d >= sevenDaysAgo;
          });
        };

        const pendingOrders = allOrders.filter(o => o.status === 'bekleyen');
        const terminVerildiOrders = allOrders.filter(o => o.status === 'termin_verildi');
        const dijitalBaskiOrders = allOrders.filter(o => o.status === 'dijital_baski');
        const readyOrders = allOrders.filter(o => o.status === 'teslimata_hazir');
        const completedOrdersRaw = allOrders.filter(o => o.status === 'tamamlanan');
        const unavailableOrdersRaw = allOrders.filter(o => o.status === 'temin_edilemeyen');
        const cancelledOrdersRaw = allOrders.filter(o => o.status === 'iptal_edilen');

        const completedOrders = filterLastWeekIfNeeded(completedOrdersRaw, 'tamamlanan');
        const unavailableOrders = filterLastWeekIfNeeded(unavailableOrdersRaw, 'temin_edilemeyen');
        const cancelledOrders = filterLastWeekIfNeeded(cancelledOrdersRaw, 'iptal_edilen');

        const counts = {
          pending: pendingOrders.length,
          terminVerildi: terminVerildiOrders.length,
          dijitalBaski: dijitalBaskiOrders.length,
          ready: readyOrders.length,
          completed: completedOrders.length,
          unavailable: unavailableOrders.length,
          cancelled: cancelledOrders.length
        };
        setOrderCounts(counts);

        const cr = (list) => list.filter((o) => o.cancelRequestPending).length;
        setCancelRequestBadges({
          pending: cr(pendingOrders),
          terminVerildi: cr(terminVerildiOrders),
          dijitalBaski: cr(dijitalBaskiOrders),
          ready: cr(readyOrders),
          completed: cr(completedOrders),
          unavailable: cr(unavailableOrders),
          cancelled: cr(cancelledOrders)
        });
      }
    } catch (error) {
      console.error('Sipariş sayıları yüklenemedi:', error);
    }
  }, [onlyLastWeek]);

  useEffect(() => {
    loadOrderCounts();
  }, [onlyLastWeek, loadOrderCounts]);

  const handleEditOrder = (order) => {
    setOrderToEdit(order);
    setShowCreateForm(true);
  };

  const isUpdatingFromUrlRef = useRef(false); // URL'den güncelleme flag'i

  // URL'den tab parametresini oku ve aktif sekme olarak ayarla
  useEffect(() => {
    if (tabParam) {
      const tabMap = {
        'pending': 'pending',
        'terminVerildi': 'terminVerildi',
        'dijitalBaski': 'dijitalBaski',
        'ready': 'ready',
        'completed': 'completed',
        'unavailable': 'unavailable',
        'cancelled': 'cancelled'
      };
      if (tabMap[tabParam] && tabMap[tabParam] !== activeSection) {
        isUpdatingFromUrlRef.current = true;
        setActiveSection(tabMap[tabParam]);
        setTimeout(() => {
          isUpdatingFromUrlRef.current = false;
        }, 100);
      }
    }
  }, [tabParam, activeSection]);

  // URL'den güncelleme yapıldığında flag'i temizle
  useEffect(() => {
    if (isUpdatingFromUrlRef.current) {
      const timer = setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeSection]);

  const sections = [
    { id: 'create', label: 'Sipariş Oluştur', icon: Plus, color: 'blue', count: null },
    { id: 'pending', label: 'Bekleyen', icon: Clock, color: 'yellow', count: orderCounts.pending },
    { id: 'terminVerildi', label: 'Termin Verildi', icon: Calendar, color: 'purple', count: orderCounts.terminVerildi },
    { id: 'dijitalBaski', label: 'Dijital Baskı', icon: Printer, color: 'indigo', count: orderCounts.dijitalBaski },
    { id: 'ready', label: 'Teslimata Hazır', icon: Package, color: 'green', count: orderCounts.ready },
    { id: 'completed', label: 'Tamamlanan', icon: CheckCircle, color: 'green', count: orderCounts.completed },
    { id: 'unavailable', label: 'Temin Edilemeyen', icon: AlertCircle, color: 'orange', count: orderCounts.unavailable },
    { id: 'cancelled', label: 'İptal Edilen', icon: XCircle, color: 'red', count: orderCounts.cancelled },
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: {
        bg: isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-blue-600 dark:text-blue-400',
        text: isActive ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white',
        count: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
      },
      yellow: {
        bg: isActive ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-yellow-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-yellow-600 dark:text-yellow-400',
        text: isActive ? 'text-yellow-900 dark:text-yellow-100' : 'text-gray-900 dark:text-white',
        count: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
      },
      green: {
        bg: isActive ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-green-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-green-600 dark:text-green-400',
        text: isActive ? 'text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-white',
        count: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
      },
      orange: {
        bg: isActive ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-orange-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-orange-600 dark:text-orange-400',
        text: isActive ? 'text-orange-900 dark:text-orange-100' : 'text-gray-900 dark:text-white',
        count: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
      },
      red: {
        bg: isActive ? 'bg-red-50 dark:bg-red-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-red-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-red-600 dark:text-red-400',
        text: isActive ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white',
        count: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
      },
      purple: {
        bg: isActive ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-white dark:bg-gray-800',
        border: isActive ? 'border-purple-500' : 'border-gray-200 dark:border-gray-700',
        icon: 'text-purple-600 dark:text-purple-400',
        text: isActive ? 'text-purple-900 dark:text-purple-100' : 'text-gray-900 dark:text-white',
        count: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      },
    };
    return colors[color] || colors.blue;
  };

  return (
    <CustomerPortalLayout title="Siparişlerim" icon={ShoppingCart} showBackButton={true}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Cards - Mobilde 2 sütun, Desktop'ta 4 sütun */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const colors = getColorClasses(section.color, isActive);
            
            return (
              <button
                key={section.id}
                onClick={() => {
                  if (section.id === 'create') {
                    setShowCreateForm(true);
                  } else {
                    // Tab'a tıklayınca hem activeSection hem de URL'yi güncelle
                    isUpdatingFromUrlRef.current = false; // URL güncellemesine izin ver
                    setActiveSection(section.id);
                    setSearchParams({ tab: section.id });
                  }
                }}
                className={`
                  ${colors.bg} ${colors.border} border-2 rounded-lg px-2 sm:px-3 py-2 sm:py-3 text-left transition-all
                  hover:shadow-md hover:scale-[1.02] active:scale-[0.98]
                  ${isActive ? 'shadow-lg ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800' : ''}
                  flex items-center gap-1.5 sm:gap-2 h-full
                `}
              >
                <div className={`${colors.icon} p-1.5 sm:p-2 rounded-lg bg-white dark:bg-gray-700 flex-shrink-0`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <h3 className={`${colors.text} font-semibold text-xs sm:text-sm leading-tight break-words`}>
                        {section.label}
                      </h3>
                      {section.count !== null && (
                    <div className="mt-1 sm:mt-1.5 flex flex-wrap items-center gap-1">
                      {section.count > 0 ? (
                        <span className={`${colors.count} text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full inline-block`}>
                          {section.count}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                          {section.count}
                        </span>
                      )}
                      {cancelRequestBadges[section.id] > 0 && (
                        <span
                          className="text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white"
                          title="Onay bekleyen iptal talebi"
                        >
                          {cancelRequestBadges[section.id]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Section Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm sm:shadow-md p-3 sm:p-4 lg:p-6">
          {activeSection === 'pending' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Bekleyen Siparişler
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Bekleyen siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'terminVerildi' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Termin Verildi Siparişler
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-purple-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Termin verildi siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'dijitalBaski' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Dijital Baskı Siparişler
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Printer className="w-12 h-12 sm:w-16 sm:h-16 text-indigo-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Dijital baskı siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'ready' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Teslimata Hazır Siparişler
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Package className="w-12 h-12 sm:w-16 sm:h-16 text-green-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Teslimata hazır siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'completed' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Tamamlanan Siparişler
              </h3>
              <div className="flex justify-end mb-3">
                <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={onlyLastWeek}
                    onChange={(e) => setOnlyLastWeek(e.target.checked)}
                  />
                  <span>Sadece son 7 gün</span>
                </label>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Tamamlanan siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'unavailable' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Temin Edilemeyen Siparişler
              </h3>
              <div className="flex justify-end mb-3">
                <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={onlyLastWeek}
                    onChange={(e) => setOnlyLastWeek(e.target.checked)}
                  />
                  <span>Sadece son 7 gün</span>
                </label>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-orange-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-orange-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Temin edilemeyen siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'cancelled' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                İptal Edilen Siparişler
              </h3>
              <div className="flex justify-end mb-3">
                <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={onlyLastWeek}
                    onChange={(e) => setOnlyLastWeek(e.target.checked)}
                  />
                  <span>Sadece son 7 gün</span>
                </label>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <Loader className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <XCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    İptal edilen siparişiniz bulunmamaktadır.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} onRefresh={loadOrders} onOrderCountsUpdate={loadOrderCounts} onEdit={handleEditOrder} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create Order Form Modal */}
      {showCreateForm && (
        <CreateOrderForm
          orderToEdit={orderToEdit}
          onClose={() => {
            setShowCreateForm(false);
            setOrderToEdit(null);
          }}
          onSuccess={async () => {
            // Sipariş oluşturulduktan/güncellendikten sonra bekleyen siparişlere geç ve listeyi yenile
            await loadOrderCounts();
            setActiveSection('pending');
            // activeSection değiştiği için useEffect loadOrders'ı çağıracak
            setTimeout(() => loadOrders(), 100);
            setOrderToEdit(null);
          }}
        />
      )}
    </CustomerPortalLayout>
  );
};

// Order Card Component
const OrderCard = ({ order, onRefresh, onOrderCountsUpdate, onEdit }) => {
  const [cancelling, setCancelling] = useState(false);
  const [holding, setHolding] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canCancel = order.status === 'bekleyen';
  const canEdit = order.status === 'bekleyen';
  const canHold = order.status === 'teslimata_hazir' && !order.isOnHold;
  const canResume = order.isOnHold === true;
  const canRequestCancel =
    (order.status === 'termin_verildi' || order.status === 'teslimata_hazir') && !order.cancelRequestPending;
  const cancelPending = order.cancelRequestPending === true;

  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cancelRequestReason, setCancelRequestReason] = useState('');
  const [requestingCancel, setRequestingCancel] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const historyOrder = {
    ...order,
    stockCardName: order.stockCard?.name || '-',
    holdHistory: order.holdHistory || []
  };

  const handleCancel = async () => {
    if (!window.confirm('Bu siparişi iptal etmek istediğinize emin misiniz?')) {
      return;
    }

    setCancelling(true);
    try {
      await customerPortalService.cancelOrder(order.id);
      toast.success('Sipariş iptal edildi');
      onRefresh && onRefresh();
      // Sipariş sayılarını güncelle
      onOrderCountsUpdate && onOrderCountsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sipariş iptal edilemedi');
    } finally {
      setCancelling(false);
    }
  };

  const handleHold = async () => {
    setShowHoldModal(true);
  };

  const handleHoldConfirm = async () => {
    setHolding(true);
    try {
      await customerPortalService.holdOrder(order.id);
      toast.success('Sipariş bekletildi');
      setShowHoldModal(false);
      onRefresh && onRefresh();
      onOrderCountsUpdate && onOrderCountsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sipariş bekletilemedi');
    } finally {
      setHolding(false);
    }
  };

  const handleResume = async () => {
    setShowResumeModal(true);
  };

  const submitCancelRequest = async () => {
    const trimmed = (cancelRequestReason || '').trim();
    if (!trimmed) {
      toast.error('İptal nedenini yazmanız zorunludur.');
      return;
    }
    setRequestingCancel(true);
    try {
      await customerPortalService.requestCancelOrder(order.id, trimmed);
      toast.success('İptal talebiniz iletildi');
      setShowCancelRequestModal(false);
      setCancelRequestReason('');
      onRefresh && onRefresh();
      onOrderCountsUpdate && onOrderCountsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'İptal talebi gönderilemedi');
    } finally {
      setRequestingCancel(false);
    }
  };

  const handleResumeConfirm = async () => {
    setResuming(true);
    try {
      await customerPortalService.resumeOrder(order.id);
      toast.success('Sipariş devam ettirildi');
      setShowResumeModal(false);
      onRefresh && onRefresh();
      onOrderCountsUpdate && onOrderCountsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sipariş devam ettirilemedi');
    } finally {
      setResuming(false);
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleImageClick = () => {
    if (order.mediantegImageUrl) {
      setShowImageModal(true);
      setImageScale(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleZoomIn = (e) => {
    e.stopPropagation();
    setImageScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    setImageScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleMouseDown = (e) => {
    if (imageScale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y
      });
    }
  };


  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  useEffect(() => {
    if (showImageModal) {
      const handleMouseMoveGlobal = (e) => {
        if (isDragging && imageScale > 1) {
          setImagePosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
          });
        }
      };

      const handleMouseUpGlobal = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleMouseMoveGlobal);
      document.addEventListener('mouseup', handleMouseUpGlobal);
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveGlobal);
        document.removeEventListener('mouseup', handleMouseUpGlobal);
      };
    }
  }, [showImageModal, isDragging, dragStart, imageScale]);

  return (
    <>
      {/* Mobil: Dikey kart, Desktop: Yatay kart */}
      <div className={`border-2 rounded-lg hover:shadow-sm transition-shadow ${
        order.isOnHold 
          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-500' 
          : cancelPending
            ? 'bg-gray-100 dark:bg-gray-800/80 border-gray-300 dark:border-gray-600'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
      }`}>
        {/* Mobil: Dikey düzen */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-3">
          {/* Görsel - Mobilde daha büyük */}
          {order.mediantegImageUrl ? (
            <img
              src={order.mediantegImageUrl}
              alt={order.stockCard?.name || 'Sipariş görseli'}
              className="w-full h-48 sm:w-20 sm:h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-600 flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleImageClick}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-48 sm:w-20 sm:h-20 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-8 h-8 sm:w-6 sm:h-6 text-gray-400 dark:text-gray-500" />
            </div>
          )}

          {/* İçerik */}
          <div className="flex-1 min-w-0">
            {/* Stok Adı */}
            <h4 className="font-semibold text-base sm:text-sm text-gray-900 dark:text-white mb-1.5">
              {order.stockCard?.name || 'Stok Kartı'}
            </h4>
            
            {/* Renk ve Miktar - Mobilde daha belirgin */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
              {order.color && (
                <div className="flex items-center gap-1.5">
                  <ColorDot colorName={order.color} size="sm" />
                  <span className="text-sm sm:text-xs text-gray-700 dark:text-gray-300 font-medium">{order.color}</span>
                </div>
              )}
              <span className="text-sm sm:text-xs text-gray-700 dark:text-gray-300 font-medium">
                {order.quantity} {order.unit?.shortName || order.unit?.name || ''}
              </span>
            </div>

            {/* Termin Tarihi - Mobilde daha belirgin */}
            {order.status === 'termin_verildi' && (order.terminDate || order.terminVerildiDate) && (
              <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1">
                Termin: {formatDateOnly(order.terminDate || order.terminVerildiDate)}
              </div>
            )}

            {/* Medianteg bilgileri - Mobilde ve Desktop'ta göster */}
            <div className="flex items-center gap-2 sm:gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
              {order.mediantegOrderNo && (
                <>
                  <span>Sipariş: {order.mediantegOrderNo}</span>
                </>
              )}
              {order.mediantegModelNo && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span>Model: {order.mediantegModelNo}</span>
                </>
              )}
              {order.mediantegOrderType && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span>Tip: {order.mediantegOrderType}</span>
                </>
              )}
            </div>

            {/* Açıklama - Mobilde ve Desktop'ta göster */}
            {order.description && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {order.description}
              </div>
            )}
          </div>

          {/* Butonlar - Mobilde alt kısımda, Desktop'ta sağda */}
          <div className="flex sm:flex-col gap-2 sm:gap-1.5 sm:flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setShowHistoryModal(true)}
              className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded transition-colors flex items-center justify-center gap-1.5 sm:gap-1"
              title="Sipariş geçmişi"
            >
              <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              <span className="sm:hidden">Geçmiş</span>
            </button>
            {canEdit && (
              <>
                <button
                  onClick={() => onEdit && onEdit(order)}
                  className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors flex items-center justify-center gap-1.5 sm:gap-1"
                >
                  <Edit className="w-4 h-4 sm:w-3 sm:h-3" />
                  <span>Düzenle</span>
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'İptal...' : 'İptal Et'}
                </button>
              </>
            )}
            {canCancel && !canEdit && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full sm:w-auto px-3 py-2 sm:px-2 sm:py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
              >
                {cancelling ? 'İptal...' : 'İptal Et'}
              </button>
            )}
            {canHold && (
              <button
                onClick={handleHold}
                disabled={holding}
                className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 sm:gap-1"
              >
                {holding ? 'Bekletiliyor...' : 'Beklet'}
              </button>
            )}
            {canResume && (
              <button
                onClick={handleResume}
                disabled={resuming}
                className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 sm:gap-1"
              >
                {resuming ? 'Devam Ettiriliyor...' : 'Devam Ettir'}
              </button>
            )}
            {canRequestCancel && (
              <button
                type="button"
                onClick={() => setShowCancelRequestModal(true)}
                className="flex-1 sm:flex-none px-3 py-2 sm:px-2 sm:py-1 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors border border-amber-300 dark:border-amber-700"
              >
                İptal talep et
              </button>
            )}
            {cancelPending && (
              <div className="flex-1 sm:flex-none px-2 py-1.5 text-[11px] sm:text-xs text-center text-gray-600 dark:text-gray-400 font-medium">
                İptal talebi bekleniyor
              </div>
            )}
          </div>
        </div>

      {/* Durum Notları - Tüm notlar gösteriliyor */}
      {(order.bekleyenNote || order.terminVerildiNote || order.dijitalBaskiNote || order.teslimataHazirNote || order.tamamlananNote || order.teminEdilemeyenNote || order.iptalEdilenNote) && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 px-3 sm:px-3 space-y-1.5">
          {order.bekleyenNote && (
            <div className="text-xs bg-yellow-50 dark:bg-yellow-900/20 border-l-2 border-yellow-400 px-2 py-1 rounded">
              <span className="font-medium text-yellow-800 dark:text-yellow-200">Bekleyen Not:</span>
              <span className="text-yellow-700 dark:text-yellow-300 ml-1">{order.bekleyenNote}</span>
            </div>
          )}
          {order.terminVerildiNote && (
            <div className="text-xs bg-purple-50 dark:bg-purple-900/20 border-l-2 border-purple-400 px-2 py-1 rounded">
              <span className="font-medium text-purple-800 dark:text-purple-200">Termin Verildi Not:</span>
              <span className="text-purple-700 dark:text-purple-300 ml-1">{order.terminVerildiNote}</span>
            </div>
          )}
          {order.dijitalBaskiNote && (
            <div className="text-xs bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-indigo-400 px-2 py-1 rounded">
              <span className="font-medium text-indigo-800 dark:text-indigo-200">Dijital Baskı Not:</span>
              <span className="text-indigo-700 dark:text-indigo-300 ml-1">{order.dijitalBaskiNote}</span>
            </div>
          )}
          {order.teslimataHazirNote && (
            <div className="text-xs bg-green-50 dark:bg-green-900/20 border-l-2 border-green-400 px-2 py-1 rounded">
              <span className="font-medium text-green-800 dark:text-green-200">Teslimata Hazır Not:</span>
              <span className="text-green-700 dark:text-green-300 ml-1">{order.teslimataHazirNote}</span>
            </div>
          )}
          {order.tamamlananNote && (
            <div className="text-xs bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-400 px-2 py-1 rounded">
              <span className="font-medium text-blue-800 dark:text-blue-200">Tamamlanan Not:</span>
              <span className="text-blue-700 dark:text-blue-300 ml-1">{order.tamamlananNote}</span>
            </div>
          )}
          {order.teminEdilemeyenNote && (
            <div className="text-xs bg-orange-50 dark:bg-orange-900/20 border-l-2 border-orange-400 px-2 py-1 rounded">
              <span className="font-medium text-orange-800 dark:text-orange-200">Temin Edilemeyen Not:</span>
              <span className="text-orange-700 dark:text-orange-300 ml-1">{order.teminEdilemeyenNote}</span>
            </div>
          )}
          {order.iptalEdilenNote && (
            <div className="text-xs bg-red-50 dark:bg-red-900/20 border-l-2 border-red-400 px-2 py-1 rounded">
              <span className="font-medium text-red-800 dark:text-red-200">İptal Edilen Not:</span>
              <span className="text-red-700 dark:text-red-300 ml-1">{order.iptalEdilenNote}</span>
            </div>
          )}
        </div>
      )}
      </div>

      {/* İptal talebi modalı */}
      {showCancelRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              İptal talebi
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              İptal nedeninizi yazın (zorunlu). Talebiniz tedarikçi onayına gönderilecektir.
            </p>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              İptal nedeni <span className="text-red-600">*</span>
            </label>
            <textarea
              value={cancelRequestReason}
              onChange={(e) => setCancelRequestReason(e.target.value)}
              rows={4}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-4"
              placeholder="İptal nedenini açıklayın"
            />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowCancelRequestModal(false);
                  setCancelRequestReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={submitCancelRequest}
                disabled={requestingCancel}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
              >
                {requestingCancel ? 'Gönderiliyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bekletme Onay Modalı */}
      {showHoldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Siparişi Beklet
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Bu siparişi bekletmek istediğinize emin misiniz? Bekletilen siparişler turuncu renkle işaretlenecektir.
              </p>
              <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-3 rounded">
                <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                  ⚠️ Önemli: Bekletilen siparişler siz tekrar devam ettirilene kadar size teslim edilemeyecektir.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowHoldModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleHoldConfirm}
                disabled={holding}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors disabled:opacity-50"
              >
                {holding ? 'Bekletiliyor...' : 'Evet, Beklet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Devam Ettirme Onay Modalı */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Siparişi Devam Ettir
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Bu siparişi devam ettirmek istediğinize emin misiniz? Sipariş normale dönecek ve admin tarafından durum değişikliği yapılabilecektir.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResumeModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleResumeConfirm}
                disabled={resuming}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
              >
                {resuming ? 'Devam Ettiriliyor...' : 'Evet, Devam Ettir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resim Modal */}
      {showImageModal && order.mediantegImageUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <div className="relative max-w-full max-h-full">
            {/* Zoom Kontrolleri */}
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={handleZoomIn}
                className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Yakınlaştır"
              >
                <ZoomIn className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={handleZoomOut}
                className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Uzaklaştır"
              >
                <ZoomOut className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={handleCloseModal}
                className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Kapat"
              >
                <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
            </div>

            {/* Resim */}
            <img
              src={order.mediantegImageUrl}
              alt={order.stockCard?.name || 'Sipariş görseli'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl cursor-move"
              style={{
                transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onWheel={handleWheel}
              draggable={false}
            />
      </div>
    </div>
      )}

      <CustomerOrderHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        order={historyOrder}
      />
    </>
  );
};

export default CustomerOrders;

